// Renders Cloudflare Turnstile widget and guards parent form submission
// until the verification token is present
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    pendingMessage: { type: String, default: "Please try again in a moment — we're still verifying your session." },
    loadErrorMessage: { type: String, default: "Security verification failed to load. Please refresh the page and try again." },
    errorMessage: { type: String, default: "Security verification could not be completed. Please try again." }
  }

  static MAX_RENDER_ATTEMPTS = 50

  connect() {
    const sitekey = this.element.dataset.sitekey

    if (this.element.classList.contains('cf-turnstile') && sitekey) {
      this.#renderWhenReady()
    }

    this.form = this.element.closest('form')
    if (this.form && sitekey) {
      this.boundGuardSubmit = this.#guardSubmit.bind(this)
      this.form.addEventListener('submit', this.boundGuardSubmit)
    }
  }

  disconnect() {
    if (this.renderTimer) {
      clearInterval(this.renderTimer)
      this.renderTimer = null
    }
    if (this.form && this.boundGuardSubmit) {
      this.form.removeEventListener('submit', this.boundGuardSubmit)
    }
  }

  #renderWhenReady() {
    if (this.element.querySelector('[name=cf-turnstile-response]')) return

    if (window.turnstile) {
      this.#renderWidget()
      return
    }

    // Poll every 100ms for up to 5 seconds (50 attempts)
    let attempts = 0
    this.renderTimer = setInterval(() => {
      attempts++
      if (window.turnstile) {
        clearInterval(this.renderTimer)
        this.renderTimer = null
        this.#renderWidget()
      } else if (attempts >= this.constructor.MAX_RENDER_ATTEMPTS) {
        clearInterval(this.renderTimer)
        this.renderTimer = null
        console.error('Turnstile verification script failed to load')
        this.#showLoadError()
      }
    }, 100)
  }

  #renderWidget() {
    this.widgetId = window.turnstile.render(this.element, {
      'error-callback': (errorCode) => {
        console.error(`Turnstile challenge failed: ${errorCode}`)
        this.#showError()
      },
      'expired-callback': () => {
        window.turnstile.reset(this.widgetId)
      },
      'timeout-callback': () => {
        window.turnstile.reset(this.widgetId)
      }
    })
  }

  #guardSubmit(event) {
    const token = this.form.querySelector("[name='cf-turnstile-response']")

    if (!token || !token.value) {
      event.preventDefault()

      const message = this.#findOrCreateMessage()
      message.textContent = this.pendingMessageValue
      message.hidden = false

      // Rails UJS disables submit buttons via a 13ms setTimeout after the
      // submit event. Re-enable after that timer fires so the user can retry.
      setTimeout(() => {
        this.form.querySelectorAll('[type="submit"]').forEach(btn => btn.disabled = false)
      }, 20)
    } else {
      const message = this.form.querySelector('[data-turnstile-pending-message]')
      if (message) message.hidden = true
    }
  }

  #showLoadError() {
    this.#showMessage(this.loadErrorMessageValue)
  }

  #showError() {
    this.#showMessage(this.errorMessageValue)
  }

  #showMessage(text) {
    if (!this.form) return

    const message = this.#findOrCreateMessage()
    message.textContent = text
    message.hidden = false
  }

  #findOrCreateMessage() {
    let message = this.form.querySelector('[data-turnstile-pending-message]')
    if (!message) {
      message = document.createElement('p')
      message.setAttribute('data-turnstile-pending-message', '')
      message.className = 'errors'
      message.hidden = true

      const submitBtn = this.form.querySelector('[type="submit"]')
      submitBtn ? submitBtn.before(message) : this.form.appendChild(message)
    }
    return message
  }
}
